import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from '../utils/axiosInstances';
import { LOCAL_AUTH_JWT } from '../utils/constants';
import { useSettings } from './SettingsContext';
import { useLocation } from 'react-router';
import { HOME_PATH, TEST_VIEW_ALL_PATH } from '../utils/paths';
import { getFeedbackWindowMs } from '../utils/dateTimeUtils';
import { loadDashboardFeedbackSession, persistDashboardFeedbackSession } from '../utils/dashboardFeedbackSession';
import useRealtimeTestsListSocket from '../hooks/useRealtimeTestsListSocket';

const DashboardContext = createContext();

export const useDashboardData = () => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error('useDashboardData must be used within a DashboardProvider');
    }
    return context;
};

/** Returns null when outside DashboardProvider (e.g. legacy TestView). */
export const useOptionalDashboardData = () => useContext(DashboardContext);

export const DashboardProvider = ({ children }) => {
    const persisted = useMemo(() => loadDashboardFeedbackSession(), []);

    const pendingFeedbackIdsRef = useRef(new Set(persisted?.pendingSurveyTestIds?.map(String) ?? []));
    const [pendingSurveyTestIds, setPendingSurveyTestIds] = useState(() => persisted?.pendingSurveyTestIds ?? []);
    const [feedbackAlertHoldIds, setFeedbackAlertHoldIds] = useState([]);
    const postSurveyKeepPinnedIdsRef = useRef(persisted?.postSurveyKeep ?? new Set());
    const feedbackWindowEndMsRef = useRef({ ...(persisted?.deadlines ?? {}) });
    const autoFillEnabledRef = useRef(true);

    const location = useLocation();
    const isCentralMonitorRoute = location.pathname === HOME_PATH || location.pathname === TEST_VIEW_ALL_PATH;

    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [pinnedTestIds, setPinnedTestIds] = useState(() => persisted?.pinnedTestIds ?? []);
    const MAX_PINNED_TESTS = 6;

    const [endedTestSnapshots, setEndedTestSnapshots] = useState(() => persisted?.endedTestSnapshots ?? {});

    const { testGrids } = useSettings();

    useEffect(() => {
        autoFillEnabledRef.current = true;
    }, [testGrids]);

    useEffect(() => {
        if (!isCentralMonitorRoute) return;
        persistDashboardFeedbackSession({
            deadlines: { ...feedbackWindowEndMsRef.current },
            pendingSurveyTestIds,
            endedTestSnapshots,
            pinnedTestIds,
            postSurveyKeep: [...postSurveyKeepPinnedIdsRef.current],
        });
    }, [isCentralMonitorRoute, pendingSurveyTestIds, endedTestSnapshots, pinnedTestIds]);

    const registerPendingFeedback = useCallback((test, feedbackWindowMinutes = 5) => {
        if (!test || test.id == null) return;
        const id = String(test.id);
        pendingFeedbackIdsRef.current.add(id);
        setPendingSurveyTestIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
        setFeedbackAlertHoldIds((prev) => prev.filter((x) => x !== id));
        const windowMs = getFeedbackWindowMs(feedbackWindowMinutes);
        if (feedbackWindowEndMsRef.current[id] == null) {
            feedbackWindowEndMsRef.current[id] = Date.now() + windowMs;
        }
        setEndedTestSnapshots((prev) => ({ ...prev, [id]: { ...test, ongoing: false } }));
    }, []);

    const getFeedbackDeadlineMs = useCallback((testId) => {
        if (testId == null) return null;
        return feedbackWindowEndMsRef.current[String(testId)] ?? null;
    }, []);

    const completeFeedback = useCallback((testId) => {
        const id = String(testId);
        pendingFeedbackIdsRef.current.delete(id);
        setPendingSurveyTestIds((prev) => prev.filter((x) => String(x) !== id));
        postSurveyKeepPinnedIdsRef.current.add(id);
        delete feedbackWindowEndMsRef.current[id];
        setFeedbackAlertHoldIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }, []);

    const dismissSurveyFromPinned = useCallback((testId) => {
        const id = String(testId);
        pendingFeedbackIdsRef.current.delete(id);
        setPendingSurveyTestIds((prev) => prev.filter((x) => String(x) !== id));
        postSurveyKeepPinnedIdsRef.current.delete(id);
        delete feedbackWindowEndMsRef.current[id];
        setFeedbackAlertHoldIds((prev) => prev.filter((x) => String(x) !== id));
        setPinnedTestIds((prev) => prev.filter((pid) => String(pid) !== id));
        setEndedTestSnapshots((prev) => {
            if (!prev[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    }, []);

    const applyTestsListUpdate = useCallback((activeTests) => {
        const list = Array.isArray(activeTests) ? activeTests : [];
        const activeIds = new Set(list.map(t => String(t.id)));

        setPinnedTestIds(prev => prev.filter(id =>
            activeIds.has(String(id))
            || pendingFeedbackIdsRef.current.has(String(id))
            || postSurveyKeepPinnedIdsRef.current.has(String(id))
        ));

        const enrichedTests = list.map((test) => ({
            ...test,
            vitals: { fhr: '--', ua: '--', mhr: '--', startTime: null, history: [] },
            analysis: null,
            chat: [],
            ongoing: true,
        }));

        setTests(enrichedTests);
        setFeedbackAlertHoldIds((prev) => prev.filter((hid) => activeIds.has(String(hid))));
        setError(null);
        setLoading(false);
    }, []);

    const fetchData = useCallback(async () => {
        const config = {
            headers: { Authorization: `Bearer ${localStorage.getItem(LOCAL_AUTH_JWT)}` }
        };

        try {
            const testsRes = await axios.get('/realtime-tests', config);

            if (testsRes.status !== 200 || !testsRes.data.tests) {
                throw new Error('Failed to fetch tests');
            }

            applyTestsListUpdate(testsRes.data.tests);
        } catch (err) {
            console.error('Dashboard tests fetch error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [applyTestsListUpdate]);

    // Initial REST fetch — fires once when entering the central monitor view
    // (either the home route or the "View All" route, which renders the same
    // CentralMonitorView). From then on the socket effect below keeps the list
    // in sync with server pushes.
    useEffect(() => {
        if (!isCentralMonitorRoute) return;
        fetchData();
    }, [fetchData, isCentralMonitorRoute]);

    // Socket subscription — receives `tests-list-updated` pushes when a new
    // realtime test is uploaded or an existing one auto-completes. The server
    // also emits once on connect/reconnect so we never need a polling fallback.
    useRealtimeTestsListSocket(applyTestsListUpdate, isCentralMonitorRoute);

    useEffect(() => {
        const cap = Math.min(MAX_PINNED_TESTS, Math.max(1, Number(testGrids) || MAX_PINNED_TESTS));
        setPinnedTestIds((prev) => {
            const next = prev.map(String).slice(0, cap);
            if (autoFillEnabledRef.current) {
                for (const { id } of tests) {
                    if (next.length >= cap) break;
                    const sid = String(id);
                    if (!next.includes(sid)) next.push(sid);
                }
            }

            if (next.length === prev.length && next.every((id, idx) => id === String(prev[idx]))) {
                return prev;
            }
            return next;
        });
    }, [testGrids, tests]);

    const knownTestIdsRef = React.useRef(null);
    useEffect(() => {
        if (tests.length === 0) return;
        if (knownTestIdsRef.current === null) {
            knownTestIdsRef.current = new Set(tests.map(t => String(t.id)));
            return;
        }
        const newIds = tests.map(t => String(t.id)).filter(id => !knownTestIdsRef.current.has(id));
        tests.forEach(t => knownTestIdsRef.current.add(String(t.id)));
        if (newIds.length === 0) return;
        setPinnedTestIds(prev => {
            let next = [...prev];
            newIds.forEach(id => {
                if (!next.includes(id) && next.length < Number(testGrids)) next.push(id);
            });
            return next;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tests]);

    const handlePinTest = (testId) => {
        const id = String(testId);
        setPinnedTestIds(prev => {
            if (prev.some(pid => String(pid) === id)) {
                return prev; // already pinned — no-op to avoid race with auto-fill toggling it off
            }
            postSurveyKeepPinnedIdsRef.current.add(id);
            setFeedbackAlertHoldIds((h) => h.filter((x) => String(x) !== id));
            if (prev.length >= MAX_PINNED_TESTS) {
                return [...prev.slice(1), id];
            }
            return [...prev, id];
        });
    };

    const handleUnpinTest = (testId) => {
        autoFillEnabledRef.current = false;
        const id = String(testId);
        const deadline = feedbackWindowEndMsRef.current[id];
        const windowStillOpen = deadline != null && deadline > Date.now();
        const stillNeedsSurvey = pendingFeedbackIdsRef.current.has(id);

        if (stillNeedsSurvey && windowStillOpen) {
            setPinnedTestIds((prev) => prev.filter((pid) => String(pid) !== id));
            postSurveyKeepPinnedIdsRef.current.delete(id);
            setFeedbackAlertHoldIds((h) => h.filter((x) => String(x) !== id));
            pendingFeedbackIdsRef.current.add(id);
            setPendingSurveyTestIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
            return;
        }

        setPinnedTestIds((prev) => prev.filter((pid) => String(pid) !== id));
        pendingFeedbackIdsRef.current.delete(id);
        setPendingSurveyTestIds((p) => p.filter((x) => String(x) !== id));
        setFeedbackAlertHoldIds((h) => h.filter((x) => String(x) !== id));
        postSurveyKeepPinnedIdsRef.current.delete(id);
        setEndedTestSnapshots((prev) => {
            if (!prev[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const pinnedTests = useMemo(() => {
        return pinnedTestIds.map((pid) => {
            const id = String(pid);
            const live = tests.find(t => String(t.id) === id);
            if (live) return live;
            return endedTestSnapshots[id] || null;
        }).filter(Boolean);
    }, [tests, pinnedTestIds, endedTestSnapshots]);

    const value = {
        tests,
        loading,
        error,
        refresh: fetchData,
        pinnedTests,
        pinnedTestIds,
        endedTestSnapshots,
        pendingSurveyTestIds,
        feedbackAlertHoldIds,
        handlePinTest,
        handleUnpinTest,
        registerPendingFeedback,
        getFeedbackDeadlineMs,
        completeFeedback,
        dismissSurveyFromPinned,
        maxPinnedTests: MAX_PINNED_TESTS
    };

    return (
        <DashboardContext.Provider value={value}>
            {children}
        </DashboardContext.Provider>
    );
};
